import { useState, useEffect } from "react";
import { X, Info, AlertTriangle, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export type BannerTheme = 'info' | 'warning' | 'success' | 'error';

export interface InfoBannerData {
  id: string;
  theme: BannerTheme;
  content: string;
  link_url?: string;
  link_text?: string;
  is_dismissible: boolean;
}

interface InfoBannerProps {
  banner: InfoBannerData;
  onDismiss?: (bannerId: string) => void;
}

const themeConfig = {
  info: {
    bgColor: "bg-[#1b2838]",
    bgLight: "bg-[#1b2838]",
    borderColor: "border-blue-500",
    textColor: "text-blue-600",
    icon: Info,
    iconColor: "text-blue-600"
  },
  warning: {
    bgColor: "bg-yellow-500/10",
    bgLight: "bg-yellow-500/10",
    borderColor: "border-yellow-500",
    textColor: "text-yellow-600",
    icon: AlertTriangle,
    iconColor: "text-yellow-600"
  },
  success: {
    bgColor: "bg-green-500/10",
    bgLight: "bg-green-500/10",
    borderColor: "border-green-500",
    textColor: "text-green-600",
    icon: CheckCircle,
    iconColor: "text-green-600"
  },
  error: {
    bgColor: "bg-destructive/10",
    bgLight: "bg-destructive/10",
    borderColor: "border-red-500",
    textColor: "text-red-600",
    icon: AlertCircle,
    iconColor: "text-red-600"
  }
};

export const InfoBanner = ({ banner, onDismiss }: InfoBannerProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const theme = themeConfig[banner.theme];
  const Icon = theme.icon;

  useEffect(() => {
    // Check if banner was dismissed (stored in localStorage)
    if (banner.is_dismissible) {
      const dismissedKey = `banner_dismissed_${banner.id}`;
      const isDismissed = localStorage.getItem(dismissedKey) === 'true';
      setIsVisible(!isDismissed);
    }
  }, [banner.id, banner.is_dismissible]);

  const handleDismiss = () => {
    if (banner.is_dismissible) {
      const dismissedKey = `banner_dismissed_${banner.id}`;
      localStorage.setItem(dismissedKey, 'true');
      setIsVisible(false);
      onDismiss?.(banner.id);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`w-full ${theme.bgLight}`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex justify-center items-center gap-3 flex-1 min-w-0">
            <Icon className={`w-5 h-5 ${theme.iconColor} flex-shrink-0`} />
            <div 
              className={`${theme.textColor} text-sm leading-relaxed [&_p]:w-fit w-fit`}
              dangerouslySetInnerHTML={{ __html: banner.content }}
            />
            {banner.link_url && banner.link_text && (
              <a
                href={banner.link_url}
                className={`${theme.textColor} hover:underline font-semibold text-sm whitespace-nowrap flex-shrink-0`}
              >
                {banner.link_text} →
              </a>
            )}
          </div>
          {banner.is_dismissible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className={`${theme.textColor} hover:${theme.bgColor} hover:text-white h-6 w-6 p-0 flex-shrink-0`}
              aria-label="Fermer le bandeau"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

